<template>
  <div class="fo-health">
    <div class="fo-card-loading loading loading-lg" v-if="loading"></div>

    <div class="fo-kpi" :class="result.value < 0 ? 'fo-kpi-red' : 'fo-kpi-green'">
      <span class="fo-label">Resultado do período · {{ regimeLabel }}</span>
      <strong class="fo-kpi-value fo-num" :class="result.value < 0 ? 'is-negative' : 'is-positive'">
        {{ money(result.value) }}
      </strong>
      <span class="fo-hint">{{ comparison }}</span>
      <span class="fo-hint" v-if="result.margin !== null">margem {{ percent(result.margin) }}</span>
    </div>

    <div class="fo-kpi fo-kpi-amber">
      <span class="fo-label">Inadimplência</span>
      <strong class="fo-kpi-value fo-num" :class="{ 'is-empty': delinquencyRate === null }">
        {{ delinquencyRate === null ? '—' : percent(delinquencyRate) }}
      </strong>
      <span class="fo-hint">vencido e não recebido ÷ total que venceu no período</span>
      <span class="fo-hint fo-kpi-detail" v-if="delinquencySplit">{{ delinquencySplit }}</span>
      <span class="fo-hint fo-kpi-detail" v-else-if="!delinquency">
        nada venceu ainda no período
      </span>
    </div>

    <div class="fo-kpi fo-kpi-blue">
      <span class="fo-label">Cobertura de caixa · 4 semanas</span>
      <strong class="fo-kpi-value fo-num" :class="{ 'is-empty': coverage.ratio === null }">
        {{ coverageLabel }}
        <span class="fo-pill" :class="coverageTone.pill" v-if="coverage.ratio !== null">
          {{ coverageTone.label }}
        </span>
      </strong>
      <span class="fo-hint">saldo + a receber ÷ a pagar nas próximas 4 semanas</span>
    </div>

    <div class="fo-kpi fo-kpi-primary">
      <span class="fo-label">Ticket médio por atendimento</span>
      <strong class="fo-kpi-value fo-num" :class="{ 'is-empty': ticket.value === null }">
        {{ ticket.value === null ? '—' : money(ticket.value) }}
      </strong>
      <span class="fo-hint">{{ ticketHint }}</span>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { money, percent } from './helpers';

const COVERAGE_CAP = 50;

export default {
  props: {
    result: {
      type: Object,
      required: true,
    },
    delinquency: {
      type: Object,
      default: null,
    },
    coverage: {
      type: Object,
      required: true,
    },
    ticket: {
      type: Object,
      required: true,
    },
    period: {
      type: Object,
      required: true,
    },
    regime: {
      type: String,
      default: 'competence',
    },
    hidden: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    regimeLabel() {
      return this.regime === 'cash' ? 'caixa' : 'competência';
    },
    delinquencyRate() {
      return this.delinquency ? this.delinquency.rate : null;
    },
    previousLabel() {
      const start = moment(this.period.startDate, 'YYYY-MM-DD');
      const end = moment(this.period.endDate, 'YYYY-MM-DD');
      const fullMonth = start.isSame(start.clone().startOf('month'), 'day')
        && end.isSame(start.clone().endOf('month'), 'day');
      return fullMonth ? start.clone().subtract(1, 'month').format('MMMM') : 'o período anterior';
    },
    comparison() {
      const { previous } = this.result;
      if (!previous) {
        return '';
      }
      if (previous.variation === null) {
        return `${this.previousLabel}: ${money(previous.value, this.hidden)}`;
      }
      const sign = previous.variation > 0 ? '+' : '';
      return `${sign}${percent(previous.variation)} vs ${this.previousLabel} (${money(previous.value, this.hidden)})`;
    },
    delinquencySplit() {
      if (!this.delinquency) {
        return '';
      }
      const parts = [];
      if (this.delinquency.patients !== null) {
        parts.push(`Pacientes ${percent(this.delinquency.patients)}`);
      }
      if (this.delinquency.insurances !== null) {
        parts.push(`Convênios ${percent(this.delinquency.insurances)}`);
      }
      return parts.join(' · ');
    },
    coverageLabel() {
      const { ratio } = this.coverage;
      if (ratio === null) {
        return 'Sem contas a pagar';
      }
      if (ratio >= COVERAGE_CAP) {
        return `${COVERAGE_CAP}×+`;
      }
      return `${ratio.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}×`;
    },
    coverageTone() {
      const { ratio } = this.coverage;
      if (ratio < 1) {
        return { label: 'apertada', pill: 'fo-pill-red' };
      }
      if (ratio < 1.5) {
        return { label: 'atenção', pill: 'fo-pill-amber' };
      }
      return { label: 'folgada', pill: 'fo-pill-green' };
    },
    ticketHint() {
      const { attendances } = this.ticket;
      if (!attendances) {
        return 'sem atendimentos no período';
      }
      const label = attendances === 1 ? '1 atendimento' : `${attendances.toLocaleString('pt-BR')} atendimentos`;
      return `receita de atendimentos ÷ ${label}`;
    },
  },
  methods: {
    money(value) {
      return money(value, this.hidden);
    },
    percent,
  },
};
</script>
